// Service lines and routes (docs/EcoBox-Service-Lines.md, 03 §3.2, D-28). Pure functions.
//
// Two fields, two meanings. `service` ('sea' | 'air' | 'prime') is the HANDLING MODE: how cargo
// physically moves (container, air consolidation, courier). Containers, handovers, legs, reserves
// and SLAs keep reading it. `serviceLine` is the PRODUCT the customer buys. Old documents have no
// `serviceLine`; `lineOf` derives it so they stay readable without a migration.
import type { PricingService } from './pricing';

export const SERVICE_LINES = ['BB_SEA', 'BB_AIR', 'FB_SEA', 'FB_AIR', 'DOC'] as const;
export type ServiceLine = (typeof SERVICE_LINES)[number];

export const ROUTE_STATUSES = ['open', 'coming_soon'] as const;
export type RouteStatus = (typeof ROUTE_STATUSES)[number];

/** Balikbayan is for the Philippines only, Family Box for everywhere else, Documents for any country. */
export type DestinationRule = 'philippines_only' | 'not_philippines' | 'any';

export interface ServiceLineInfo {
  code: ServiceLine;
  nameEn: string;
  /** The names are brand names: Tagalog pages keep them (Filipinos say "sea" and "air" cargo). */
  nameTl: string;
  /** How this line's cargo moves unless a route says otherwise. */
  defaultHandling: PricingService;
  /** The handling modes a route of this line may use. */
  handlings: readonly PricingService[];
  destinationRule: DestinationRule;
}

export const SERVICE_LINE_INFO: Readonly<Record<ServiceLine, ServiceLineInfo>> = {
  BB_SEA: {
    code: 'BB_SEA',
    nameEn: 'EcoBox Balikbayan Sea',
    nameTl: 'EcoBox Balikbayan Sea',
    defaultHandling: 'sea',
    handlings: ['sea'],
    destinationRule: 'philippines_only',
  },
  BB_AIR: {
    code: 'BB_AIR',
    nameEn: 'EcoBox Balikbayan Air',
    nameTl: 'EcoBox Balikbayan Air',
    defaultHandling: 'air',
    handlings: ['air', 'prime'],
    destinationRule: 'philippines_only',
  },
  FB_SEA: {
    code: 'FB_SEA',
    nameEn: 'EcoBox Family Box Sea',
    nameTl: 'EcoBox Family Box Sea',
    defaultHandling: 'sea',
    handlings: ['sea'],
    destinationRule: 'not_philippines',
  },
  FB_AIR: {
    code: 'FB_AIR',
    nameEn: 'EcoBox Family Box Air',
    nameTl: 'EcoBox Family Box Air',
    defaultHandling: 'air',
    handlings: ['air', 'prime'],
    destinationRule: 'not_philippines',
  },
  DOC: {
    code: 'DOC',
    nameEn: 'EcoBox Documents',
    nameTl: 'EcoBox Documents',
    defaultHandling: 'prime',
    handlings: ['prime'],
    destinationRule: 'any',
  },
};

const PHILIPPINES = 'PH';

export function isServiceLine(value: unknown): value is ServiceLine {
  return typeof value === 'string' && (SERVICE_LINES as readonly string[]).includes(value);
}

/** The BB / FB rule: Balikbayan only to PH, Family Box never to PH, Documents anywhere. */
export function lineAllowedFor(line: ServiceLine, destinationCountry: string): boolean {
  const toPhilippines = destinationCountry === PHILIPPINES;
  switch (SERVICE_LINE_INFO[line].destinationRule) {
    case 'philippines_only':
      return toPhilippines;
    case 'not_philippines':
      return !toPhilippines;
    case 'any':
      return true;
  }
}

/**
 * Whether a route of this line may move its cargo this way: sea lines by container, air lines by air
 * consolidation or a courier, Documents by courier. A Balikbayan Sea route by air is a mistake.
 */
export function handlingAllowedFor(line: ServiceLine, handling: PricingService): boolean {
  return SERVICE_LINE_INFO[line].handlings.includes(handling);
}

/** The document id of a route: `UAE__PH__BB_SEA`. */
export function routeId(origin: string, destinationCountry: string, line: ServiceLine): string {
  return `${origin}__${destinationCountry}__${line}`;
}

/** The fields of a shipment-like document `lineOf` reads (a shipment, a quote, a pickup). */
export interface LineSource {
  serviceLine?: ServiceLine | undefined;
  service: PricingService;
  destinationCountry: string;
}

/**
 * The service line of an old or a new document: the stored `serviceLine`, else derived from the
 * handling mode and the destination.
 *   sea   -> BB_SEA if PH, else FB_SEA
 *   air   -> BB_AIR if PH, else FB_AIR
 *   prime -> DOC if PH, else FB_AIR
 * OPEN QUESTION: nothing records whether a Prime shipment is papers. A Prime piece has only a
 * free-text description, so Prime to a country other than PH is read as a Family Box (FB_AIR) and
 * Prime to PH (only ever the documents courier) as DOC. See 11, Q-33.
 */
export function lineOf(doc: LineSource): ServiceLine {
  if (doc.serviceLine !== undefined) return doc.serviceLine;
  const toPhilippines = doc.destinationCountry === PHILIPPINES;
  switch (doc.service) {
    case 'sea':
      return toPhilippines ? 'BB_SEA' : 'FB_SEA';
    case 'air':
      return toPhilippines ? 'BB_AIR' : 'FB_AIR';
    case 'prime':
      return toPhilippines ? 'DOC' : 'FB_AIR';
  }
}

/** The part of a route the rules read. */
export interface RouteLike {
  origin: string;
  destinationCountry: string;
  serviceLine: ServiceLine;
  status: RouteStatus;
  handling: PricingService;
}

export function isOpen(route: Pick<RouteLike, 'status'>): boolean {
  return route.status === 'open';
}

/** How the cargo of a line moves: the route's own handling when a route is given, else the line's default. */
export function handlingFor(
  line: ServiceLine,
  route?: Pick<RouteLike, 'handling'>,
): PricingService {
  return route ? route.handling : SERVICE_LINE_INFO[line].defaultHandling;
}

/** The routes for one origin and destination, in the canonical line order (open or coming soon). */
export function linesFor<R extends RouteLike>(
  origin: string,
  destinationCountry: string,
  routes: readonly R[],
): R[] {
  const order = (r: R) => SERVICE_LINES.indexOf(r.serviceLine);
  return routes
    .filter((r) => r.origin === origin && r.destinationCountry === destinationCountry)
    .sort((a, b) => order(a) - order(b));
}

/**
 * The routes STAFF may book from one origin to one destination country: `open` routes only, like a
 * customer (D-31, 2026-10-25, replacing D-29's "staff may book coming soon"). A route is opened in
 * Settings → Routes when it is ready; until then nobody books it.
 */
export function staffLinesFor<R extends RouteLike>(
  origin: string,
  destinationCountry: string,
  routes: readonly R[],
): R[] {
  return linesFor(origin, destinationCountry, routes).filter(isOpen);
}

/** The routes a CUSTOMER (public API, customer app, website) may book: `open` routes only. */
export function customerLinesFor<R extends RouteLike>(
  origin: string,
  destinationCountry: string,
  routes: readonly R[],
): R[] {
  return linesFor(origin, destinationCountry, routes).filter(isOpen);
}

/** The one route for an origin, destination and line, or undefined. */
export function findRoute<R extends RouteLike>(
  origin: string,
  destinationCountry: string,
  line: ServiceLine,
  routes: readonly R[],
): R | undefined {
  return routes.find(
    (r) =>
      r.origin === origin && r.destinationCountry === destinationCountry && r.serviceLine === line,
  );
}

const HANDLING_ORDER: readonly PricingService[] = ['sea', 'air', 'prime'];

/**
 * Where a screen still offers the old three-way choice (sea / air / prime): a handling mode is
 * allowed when some given route has it. Pass the result of `staffLinesFor` or `customerLinesFor`.
 */
export function handlingsOffered(routes: readonly Pick<RouteLike, 'handling'>[]): PricingService[] {
  return HANDLING_ORDER.filter((h) => routes.some((r) => r.handling === h));
}

/**
 * Rate card choice (spec §5): of the cards that already match the handling mode, those priced for
 * this service line when the request names one and any exists, else the cards that name no line
 * (the old ones, today's match on `service`). Never a card priced for another line.
 */
export function preferLine<C extends { serviceLine?: ServiceLine | undefined }>(
  cards: readonly C[],
  line: ServiceLine | undefined,
): C[] {
  if (line === undefined) return [...cards];
  const own = cards.filter((c) => c.serviceLine === line);
  // Never another line's card: a Family Box is not priced on a Balikbayan card. Cards that name no
  // line (every card before D-28) still price any line.
  return own.length > 0 ? own : cards.filter((c) => c.serviceLine === undefined);
}
